// 3rd party libs
import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useValidateUserEmailAvailableV2Mutation, useSendUserNotificationEmailMutation } from 'api';
import { useTranslation } from 'react-i18next';
import validator from 'validate.js';
import { v4 as uuidv4 } from 'uuid';

import { Typography, Input, InputLabel, Button, TooltipWrapper, Checkbox } from 'components';

import { ChangeEvent } from 'types';
import {
  UserValidationState,
  UserTypes,
  UserFormTypes,
  UserFormElement
} from 'types/user-management';

import userConstraints from './UserValidatorConfig';
import { CountryIso2, PhoneInput } from 'react-international-phone';
import { alphaNumericNameWithSpaceAndHyphen, hasTrailingHyphenOrSpace } from 'common/helpers/v1';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { InfoCircleNew } from 'icons/indexed-icons/InfoCircleNew';
import { CheckmarkLong } from 'icons/indexed-icons/CheckMarkLong';
import theme, { themeColors } from 'themes';
import { emailExclusionList } from './FormUtility';
import SendLink from 'icons/indexed-icons/SendLink';
import { ToastMsg } from 'common/components/Toast';

interface Props {
  userData: UserFormTypes;
  onUserSave?: (
    userFormElement: UserFormTypes | UserFormElement,
    isEditMode?: boolean,
    showUserForm?: boolean
  ) => void;
  isEditMode?: boolean;
  onCancelUserAdd?: (isEditMode: boolean, userId?: string) => void;
  handleFormChange?: (data: UserTypes, isValidForm: boolean, id: string | undefined) => void;
  isUserPage?: boolean;
  isAddGroupUser?: boolean;
  groupUsers?: UserFormElement[];
}

interface InputContainerProps {
  isRow?: boolean;
  paddingHorizontal?: string | number;
}

export const PhoneContainer = styled.div`
  display: flex;
  .react-international-phone-input-container {
    display: flex;
    width: 100%;
    .react-international-phone-country-selector {
      position: relative;
      .react-international-phone-country-selector-button {
        border-radius: 4px;
        margin-right: -1px;
        border-bottom-right-radius: 0;
        border-top-right-radius: 0;
        width: 40px;
        background: transparent;
        border: 1px solid #d8dde3;
      }
    }
    input {
      width: 100%;
      border: 1px solid #d8dde3;
      border-top-right-radius: 0.375rem;
      border-bottom-right-radius: 0.375rem;
      outline: none;
      color: ${themeColors.lightGrey8};
    }
    .react-international-phone-country-selector-dropdown {
      position: absolute;
      z-index: 1;
      top: var(--react-international-phone-dropdown-left, 44px);
      left: var(--react-international-phone-dropdown-left, 0);
      display: flex;
      width: 300px;
      max-height: 200px;
      flex-direction: column;
      padding: 4px 0;
      margin: 0;
      background-color: white;
      box-shadow: 2px 2px 16px rgba(0, 0, 0, 0.25);
      color: #222;
      list-style: none;
      overflow-y: scroll;
      .react-international-phone-country-selector-dropdown__list-item {
        display: flex;
        min-height: 28px;
        box-sizing: border-box;
        align-items: center;
        padding: 2px 8px;
        img {
          margin-right: 8px;
          width: 24px;
          height: 24px;
        }
      }
    }
  }
`;
const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 1rem;
  margin-bottom: 1.25rem;
`;
export const FormSection = styled.section<{ margin?: string }>`
  display: flex;
  flex-direction: column;
  margin: ${({ margin }) => margin};

  div[class*='placeholder'],
  select option:first-child {
    color: #5d6a86;
  }

  &.occurrences {
    .ui-widget__main div > div {
      &:first-child {
        width: 24%;
      }
      &:nth-child(2) {
        width: 20%;
        margin: 0 1rem;
      }
      &:last-child {
        width: 56%;
      }
    }
  }

  .ui-widget {
    width: 100%;
  }

  .ui-widget__main > p {
    font-size: 0.875rem;
    padding-left: 1rem;
  }

  .ui-widget__main {
    overflow: visible;
  }

  .ui-date-range-picker {
    max-width: 435px;
  }

  .dropdown_checkboxes {
    font-family: ${({ theme }) => `${theme.typography.family}`};
    font-size: 0.875rem;
  }

  select {
    background: none;
  }
`;
export const InputContainer = styled.div<InputContainerProps>`
  display: flex;
  flex-direction: ${(props) => (props.isRow ? 'row' : 'column')};
  gap: 0.25rem;
  justify-content: space-between;
  width: 100%;

  ${(props) =>
    props.paddingHorizontal
      ? `
    padding-left: ${props.paddingHorizontal};
    padding-right: ${props.paddingHorizontal};
  `
      : ''}

  & > div {
    padding-left: 0;
    padding-right: 0;
  }
`;
export const StyledInputLabel = styled(InputLabel)`
  align-items: center;
  color: ${({ theme }) => theme.colors.text.mutedgray};
  font-weight: ${({ theme }) => theme.typography.text.helper.weight};
  font-size: 0.875rem;
  display: flex;
`;
const ErrorText = styled.div`
  color: ${({ theme }) => theme.colors.darkRed};
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin-bottom: 0.938rem;
`;

const TextLimit = styled.div`
  color: ${({ theme }) => theme.colors.lightGrey8};
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin-bottom: 0.938rem;
`;
const SaveUser = styled.div`
  display: flex;
  margin-top: 1rem;
  margin-left: -10px;
`;
const IconWrapper = styled.div`
  margin-left: 2px;
`;
const CustomText = styled.div`
  width: 10rem;
`;

const EmailNotify = styled.label`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  .icon {
    margin: 0;
  }
  .invite-btn {
    gap: 0.5rem;
  }
`;
const UserPrompt = styled.div`
  display: flex;
  justify-content: space-between;
`;
const UserForm = ({
  userData,
  onUserSave,
  handleFormChange,
  isUserPage,
  isEditMode
}: Props): ReactElement => {
  const { t } = useTranslation(['common', 'fpns']);
  const [user, setUser] = useState<UserTypes>(userData?.user || {});
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [validationState, setValidationState] = useState<UserValidationState>(
    userData?.user?.email && userData.user?.invalidEmail
      ? { email: `${t('email_already_use')}` }
      : (userData.validationState as UserValidationState)
  );
  const abortRef = useRef<AbortController | null>(null); // Store previous abort function
  useEffect(() => {
    setUser(userData?.user || {});
  }, [userData.id, userData?.user?.invalidEmail]);

  const [validateUserEmail, { isLoading: isValidatingUserEmail }] =
    useValidateUserEmailAvailableV2Mutation();
  const [sendUserNotificationEmail] = useSendUserNotificationEmailMutation();
  let isValidUserForm = false;
  let disabledForm = true;
  if (
    (validationState.email === undefined || validationState.email === '') &&
    (validationState.phone === undefined || validationState.phone === '') &&
    (validationState.firstName === undefined || validationState.firstName === '') &&
    (validationState.lastName === undefined || validationState.lastName === '')
  ) {
    const validations = validator.validate(user, userConstraints);
    const isValid = !Object.keys(validations || {}).length;

    isValidUserForm = isValid;
  }

  if (isDirty && !isValidUserForm) {
    disabledForm = true;
  } else if (!isDirty && !isValidUserForm) {
    disabledForm = true;
  } else if (!isDirty && isValidUserForm) {
    disabledForm = true;
  } else if (isDirty && isValidUserForm) {
    disabledForm = false;
  }

  const onUserFieldUpdate = (field: keyof UserTypes, value: string | number | boolean) => {
    const _user = {
      ...user,
      [field]: value
    };
    setUser(_user);
    if (
      validationState[field] ||
      field === 'textAlert' ||
      field === 'emailAlert' ||
      field === 'email' ||
      field === 'phone'
    ) {
      setValidationState({ ...validationState, [field]: undefined });
    }
    fieldValidation(field, value);
  };
  const isPersonalDomain = (email: string): boolean => {
    return emailExclusionList.some((domain) => email.toLowerCase().includes(domain));
  };
  const onEmailBlur = useCallback(
    async (email: string) => {
      const updatedValidationState: UserValidationState = { ...validationState, email: undefined };

      // Check if valid email pattern
      let isValidEmail = true;
      const emailRegex =
        /^(?=[^@]*[A-Za-z])(?:[a-zA-Z0-9](?:[._-]?[a-zA-Z0-9])*)@(?:[a-zA-Z0-9-]+\.){1,2}[a-zA-Z]{2,5}$/i;
      const emailRegexEmojiRes = /^(?!.*[\uD800-\uDFFF]).*$/;
      if (email === '') {
        updatedValidationState.email = `${t('email_not_empty')}`;
        isValidEmail = false;
      } else if (!emailRegex.test(email as string) || !emailRegexEmojiRes.test(email as string)) {
        updatedValidationState.email = `${t('email_not_valid')}`;
        isValidEmail = false;
      } else if (isPersonalDomain(email)) {
        updatedValidationState.email = 'Personal domain is not allowed';
        isValidEmail = false;
      } else {
        // Check if email is available/taken by another user
        try {
          isValidEmail = false;
          const userFormData = { ...user, ['email']: email, invalidEmail: !isValidEmail };
          handleFormChange?.(userFormData, isValidEmail, userData.id);
          // cancel previuous api call
          if (abortRef.current) {
            abortRef.current.abort();
          }

          const request = validateUserEmail({ email, userId: user.id });
          abortRef.current = request;

          const resp = await request.unwrap(); // Await only the latest request
          isValidEmail = resp === 'valid';
          abortRef.current = null;
        } catch (error) {
          if (error.name === 'AbortError') return;
          updatedValidationState.email = `${t('email_already_use')}`;
          isValidEmail = false;
        }
      }
      setValidationState({ ...updatedValidationState });

      const userFormData = { ...user, ['email']: email, invalidEmail: !isValidEmail };
      const validations = validator.validate(userFormData, userConstraints);

      handleFormChange?.(userFormData, !validations && isValidEmail, userData.id);
    },
    [user.email, user.id, validationState]
  );

  const validatePhone = (phone: string): boolean => {
    const updatedValidationState: UserValidationState = { ...validationState, phone: undefined };
    let isValid = true;
    if (phone === '') {
      updatedValidationState.phone = undefined;
      isValid = true;
    } else if (!isValidPhoneNumber(phone)) {
      updatedValidationState.phone = `${t('phone_number_invalid')}`;
      isValid = false;
    }
    setValidationState({ ...updatedValidationState });
    return isValid;
  };
  const fieldValidation = (field: keyof UserTypes, data: string | number | boolean) => {
    const userFormData = { ...user, [field]: data };
    let validations = validator.validate(userFormData, userConstraints);
    if (!validations && (field === 'firstName' || field === 'lastName')) {
      validations = hasTrailingHyphenOrSpace(data as string);
    }
    const validatesPhone = validatePhone(userFormData.phone || '');
    const emailValid = validationState?.email || isValidatingUserEmail ? false : true;
    setValidationState({
      ...validationState,
      [field]: validations ? validations[field]?.[0] : undefined
    });
    handleFormChange?.(
      { ...user, [field]: data },
      !validations && validatesPhone && emailValid,
      userData.id
    );
  };
  const sendNotificationEmail = () => {
    // send notification email
    sendUserNotificationEmail({ email: user.email as string, userId: undefined })
      .unwrap()
      .then(() => {
        // Email sent successfully
        ToastMsg({
          heading: t('email_invitation_heading'),
          message: t('email_invitation_sent_success'),
          type: 'success',
          position: 'top-right'
        });
      })
      .catch(() => {
        ToastMsg({
          heading: t('email_invitation_heading'),
          message: t('email_invitation_sent_error'),
          type: 'error',
          position: 'top-right'
        });
      });
  };
  return (
    <FormContainer>
      <FormSection>
        <InputContainer>
          <StyledInputLabel mandatory={true}>{t('first_name')}</StyledInputLabel>
          <Input
            value={user.firstName}
            onChange={(event: ChangeEvent) => {
              setIsDirty(true);
              event.target.value.length <= 35 &&
                alphaNumericNameWithSpaceAndHyphen(event.target.value) &&
                onUserFieldUpdate('firstName', event.target.value as string);
            }}
            placeholder={t('first_name')}
          />
          <UserPrompt>
            <TextLimit>Character limit: 35 </TextLimit>
            {validationState.firstName ? (
              <ErrorText variant="helper">{validationState.firstName}</ErrorText>
            ) : (
              ''
            )}
          </UserPrompt>
        </InputContainer>
        <InputContainer>
          <StyledInputLabel mandatory={true}>{t('last_name')}</StyledInputLabel>
          <Input
            value={user.lastName}
            onChange={(event: ChangeEvent) => {
              setIsDirty(true);
              event.target.value.length <= 35 &&
                alphaNumericNameWithSpaceAndHyphen(event.target.value) &&
                onUserFieldUpdate('lastName', event.target.value as string);
            }}
            placeholder={t('last_name')}
          />
          <UserPrompt>
            <TextLimit>Character limit: 35 </TextLimit>
            {validationState.lastName ? (
              <ErrorText variant="helper">{validationState.lastName}</ErrorText>
            ) : (
              ''
            )}
          </UserPrompt>
        </InputContainer>
      </FormSection>
      <FormSection>
        <InputContainer>
          <StyledInputLabel mandatory={true}>{t('email_address')}</StyledInputLabel>
          <Input
            value={user.email}
            onChange={(event: ChangeEvent) => {
              if (event.target.value.length > 70) {
                setValidationState({
                  ...validationState,
                  email: `${t('email_length_exceeded')}`
                });
              } else {
                setIsDirty(true);
                onUserFieldUpdate('email', event.target.value.trim() as string);
                onEmailBlur(event.target.value.trim());
              }
            }}
            placeholder="xyz@example.com"
          />
          {(userData.validationState as UserValidationState).email || validationState.email ? (
            <ErrorText mb={0} variant="helper">
              {(userData.validationState as UserValidationState).email || validationState.email}
            </ErrorText>
          ) : (
            ''
          )}
        </InputContainer>
      </FormSection>
      <FormSection margin="1rem 0">
        <InputContainer>
          <StyledInputLabel>{t('cell_phone')}</StyledInputLabel>
          <PhoneContainer>
            <PhoneInput
              defaultCountry="us"
              value={user.phone || ''}
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              onChange={(phone: string, country: CountryIso2) => {
                onUserFieldUpdate('phone', phone as string);
                if (phone && phone !== user.phone) {
                  setIsDirty(true);
                } else {
                  setIsDirty(false);
                }
              }}
              disableDialCodePrefill
            />
          </PhoneContainer>
          {validationState.phone && <ErrorText variant="helper">{validationState.phone}</ErrorText>}
        </InputContainer>
      </FormSection>
      <FormSection>
        <EmailNotify>
          {user.id ? (
            <Button className="invite-btn" variant="text" onClick={sendNotificationEmail}>
              <SendLink />
              <Typography color={theme.colors.primaryBlue5} mb={0}>
                {t('email_invitation_sent')}
              </Typography>
            </Button>
          ) : (
            <>
              <Checkbox
                checked={user.emailInvite}
                margin="0"
                width={18}
                height={18}
                onChange={(e) => {
                  onUserFieldUpdate('emailInvite', e?.target?.checked);
                }}
              />
              <Typography mb={0}>{t('email_invitation_sent')}</Typography>
            </>
          )}
        </EmailNotify>
      </FormSection>
      {!isUserPage && (
        <FormSection>
          <SaveUser>
            <Button
              variant="text"
              disabled={disabledForm}
              onClick={() => {
                setIsDirty(false);
                onUserSave?.(
                  {
                    ...userData,
                    user: user,
                    validationState: validationState,
                    id: userData.id || uuidv4()
                  },
                  isEditMode
                );
              }}
            >
              <CheckmarkLong /> &nbsp; Save
            </Button>
            <TooltipWrapper
              Tooltip={
                <CustomText>You will need to click on “Save” in order to save the user.</CustomText>
              }
              textColor={theme.colors.textLightBlack}
              backGroundColor={theme.colors.white}
            >
              <IconWrapper>
                <InfoCircleNew />
              </IconWrapper>
            </TooltipWrapper>

            {!isEditMode && (
              <>
                <Button
                  variant="text"
                  disabled={disabledForm}
                  onClick={() => {
                    setIsDirty(false);
                    onUserSave?.(
                      {
                        ...userData,
                        user: user,
                        validationState: validationState,
                        id: userData.id || uuidv4()
                      },
                      isEditMode,
                      true // this param is used to show user form after adding the user.
                    );
                  }}
                >
                  <CheckmarkLong /> &nbsp; {t('save_and_add')}{' '}
                </Button>
                <TooltipWrapper
                  Tooltip={<CustomText>{t('save_and_add_info')}</CustomText>}
                  textColor={theme.colors.textLightBlack}
                  backGroundColor={theme.colors.white}
                >
                  <IconWrapper>
                    <InfoCircleNew />
                  </IconWrapper>
                </TooltipWrapper>
              </>
            )}
          </SaveUser>
        </FormSection>
      )}
    </FormContainer>
  );
};

export default UserForm;
